"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaInterpreter = void 0;
const ExpressionConstructor_1 = require("./../expression/ExpressionConstructor");
const constant_1 = require("../constant");
const FormulaInterpreterError_1 = require("../errors/FormulaInterpreterError");
/**
 * The FormulaInterpreter class is responsible for interpreting an abstract syntax tree (AST)
 * representing a mathematical or logical expression. It evaluates expressions based on provided
 * variable data and constructs appropriate expression objects for processing.
 */
class FormulaInterpreter {
    /**
     * Executes the interpretation of the AST tree and returns the evaluated result.
     * @param {INode} astTree The abstract syntax tree to be interpreted.
     * @param {T} data The variable data to use for evaluation.
     * @returns {number | string} The result of the expression evaluation.
     */
    execute(astTree, data) {
        const result = this.interpret(astTree, data).execute(data);
        return result;
    }
    /**
     * Interprets the AST tree recursively and constructs expression objects based on the node types.
     * @param {INode} astTree The abstract syntax tree to interpret.
     * @param {T} data The variable data to use for evaluation.
     * @returns {Expression<T, string | number>} The constructed expression object.
     */
    interpret(astTree, data) {
        try {
            if (astTree.isNode()) {
                const operator = astTree.operator;
                const right = this.interpret(astTree.right, data);
                const left = this.interpret(astTree.left, data);
                switch (operator) {
                    case constant_1.AdditionOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.addition(left, right);
                    case constant_1.SubtractionOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.subtraction(left, right);
                    case constant_1.MultiplicationOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.multiplication(left, right);
                    case constant_1.DivisionOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.division(left, right);
                    case constant_1.ModuloOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.modulo(left, right);
                    case constant_1.ExponentialOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.pow(left, right);
                    default:
                        throw new Error(`This operator ${operator} is not supported.`);
                }
            }
            else if (astTree.isValue()) {
                const value = astTree.value;
                if (typeof value === "number") {
                    return ExpressionConstructor_1.ExpressionConstructor.literalValue(Number(astTree.value));
                }
                else {
                    const regex = /["']([^"']+)["']/;
                    const stringValue = value.match(regex)[1];
                    return ExpressionConstructor_1.ExpressionConstructor.literalValue(stringValue);
                }
            }
            else if (astTree.isField()) {
                const fieldValue = data[String(astTree.fieldName)];
                if (fieldValue === undefined)
                    throw new FormulaInterpreterError_1.FormulaInterpreterError(`The variable ${astTree.fieldName} not defined.`);
                if (typeof fieldValue === "number") {
                    return ExpressionConstructor_1.ExpressionConstructor.fieldReference(astTree.fieldName);
                }
                else {
                    return ExpressionConstructor_1.ExpressionConstructor.fieldReference(astTree.fieldName);
                }
            }
            else if (astTree.isComparison()) {
                const comparisonOperator = astTree.operator;
                const left = this.interpret(astTree.left, data);
                const right = this.interpret(astTree.right, data);
                switch (comparisonOperator) {
                    case constant_1.GreaterThanOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.superior(left, right);
                    case constant_1.LessThanOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.inferior(left, right);
                    case constant_1.EqualOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.equality(left, right);
                    case constant_1.GreaterThanOrEqualOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.or(ExpressionConstructor_1.ExpressionConstructor.superior(left, right), ExpressionConstructor_1.ExpressionConstructor.equality(left, right));
                    case constant_1.LessThanOrEqualOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.or(ExpressionConstructor_1.ExpressionConstructor.inferior(left, right), ExpressionConstructor_1.ExpressionConstructor.equality(left, right));
                    case constant_1.LogicalOrOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.or(left, right);
                    case constant_1.LogicalAndOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.and(left, right);
                    case constant_1.NotEqualOperator:
                        return ExpressionConstructor_1.ExpressionConstructor.different(left, right);
                    default:
                        throw new FormulaInterpreterError_1.FormulaInterpreterError(`This comparison ${comparisonOperator} method is not supported`);
                }
            }
            else if (astTree.isConditional()) {
                const condition = this.interpret(astTree.condition, data);
                const isTrue = this.interpret(astTree.isTrue, data);
                const isFalse = this.interpret(astTree.isFalse, data);
                return ExpressionConstructor_1.ExpressionConstructor.condition(condition, isTrue, isFalse);
            }
            else {
                throw new FormulaInterpreterError_1.FormulaInterpreterError(`This Expression is not Correct. Please verify Your expression [Interpreter]:${astTree}`);
            }
        }
        catch (e) {
            throw e;
        }
    }
}
exports.FormulaInterpreter = FormulaInterpreter;
